(() => {
  const observedElements = new WeakSet();
  const scrollListeners = new WeakSet();
  let resizeFrame;

  const setOverflowState = (element, viewport) => {
    if (!element || !viewport) return;

    requestAnimationFrame(() => {
      const hasOverflow = viewport.scrollWidth > viewport.clientWidth + 1;
      element.dataset.hasOverflow = hasOverflow ? 'true' : 'false';
    });
  };

  const setHorizontalScrollState = (element, viewport) => {
    if (!element || !viewport) return;

    requestAnimationFrame(() => {
      const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const hasOverflow = maximumScroll > 1;
      const currentScroll = Math.max(0, Math.abs(viewport.scrollLeft));

      element.dataset.hasOverflow = hasOverflow ? 'true' : 'false';
      element.dataset.scrollStart = !hasOverflow || currentScroll <= 1 ? 'true' : 'false';
      element.dataset.scrollEnd = !hasOverflow || currentScroll >= maximumScroll - 1 ? 'true' : 'false';
    });
  };

  const observeHorizontalScroller = (container, viewport) => {
    setHorizontalScrollState(container, viewport);

    if (!scrollListeners.has(viewport)) {
      viewport.addEventListener('scroll', () => setHorizontalScrollState(container, viewport), { passive: true });
      scrollListeners.add(viewport);
    }

    if (!observedElements.has(viewport) && 'ResizeObserver' in window) {
      const observer = new ResizeObserver(() => setHorizontalScrollState(container, viewport));
      observer.observe(viewport);

      if (viewport.firstElementChild) {
        observer.observe(viewport.firstElementChild);
      }

      observedElements.add(viewport);
    }
  };

  const updateSwatches = (root = document) => {
    const containers = [
      ...(root.matches?.('.product-swatches-options') ? [root] : []),
      ...root.querySelectorAll('.product-swatches-options'),
    ];

    containers.forEach((container) => {
      const viewport = container.querySelector('.product-swatches-options__viewport');
      if (!viewport) return;

      observeHorizontalScroller(container, viewport);
    });
  };

  const updateProductSelectors = (root = document) => {
    const containers = [
      ...(root.matches?.('.product__color-swatches') ? [root] : []),
      ...root.querySelectorAll('.product__color-swatches'),
    ];

    containers.forEach((container) => {
      const viewport = container.querySelector('.product__color-swatches--inner');
      if (!viewport) return;

      container.setAttribute('data-swatch-scroll-root', '');
      observeHorizontalScroller(container, viewport);
    });
  };

  const updateSizeLists = (root = document) => {
    const lists = [
      ...(root.matches?.('[data-product-card-size-list]') ? [root] : []),
      ...root.querySelectorAll('[data-product-card-size-list]'),
    ];

    lists.forEach((list) => {
      setOverflowState(list, list);

      const productItem = list.closest('.product-item');
      if (productItem && !observedElements.has(productItem) && 'ResizeObserver' in window) {
        const observer = new ResizeObserver(() => setOverflowState(list, list));
        observer.observe(productItem);
        observedElements.add(productItem);
      }
    });
  };

  const update = (root = document) => {
    updateSwatches(root);
    updateProductSelectors(root);
    updateSizeLists(root);
  };

  document.addEventListener('pointerover', (event) => {
    const productItem = event.target.closest('.product-item');
    if (productItem) updateSizeLists(productItem);
  });

  document.addEventListener('focusin', (event) => {
    const productItem = event.target.closest('.product-item');
    if (productItem) updateSizeLists(productItem);
  });

  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => update());
  });

  document.addEventListener('shopify:section:load', (event) => update(event.target));

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) update(node);
      });
    });
  });

  const initialise = () => {
    update();
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
